import * as React from "react";
import {useEffect, useState} from "react";
import {RaisedButton} from "../common/RaisedButton";
import {prominentButtonStyle} from "../common/buttonStyles";
import {IToastNotification} from "../../utils/planUserRemoval";
import {AdminRegion, LoginContentAdminApi, Stat} from "../../api/LoginContentAdminApi";

export const ADMIN_LOGIN_STAT_CONTENT_DESCRIPTION = "These 3 stats appear at the bottom of the marketing panel.";
export const ADMIN_LOGIN_STAT_CONTENT_SAVE_BTN = "Save Stats";
export const ADMIN_LOGIN_STAT_CONTENT_SAVED_SUCCESS = "Stats saved";
export const ADMIN_LOGIN_STAT_CONTENT_SAVED_ERROR = "Failed to save stats";

interface AdminLoginStatContentProps {
    activeRegion: AdminRegion;
    setToast: (toast: IToastNotification) => void;
    onStatsSaved: (regionId: number) => void;
}

export const AdminLoginStatContent: React.FunctionComponent<AdminLoginStatContentProps> = (props) => {
    const STAT_COUNT = 3;
    const emptyStat = (): Stat => ({value: "", label: ""});
    const buildStats = (region: AdminRegion): Stat[] => {
        const filled = region.stats.slice(0, STAT_COUNT);
        return [...filled, ...Array.from({length: STAT_COUNT - filled.length}, emptyStat)];
    };
    const [stats, setStats] = useState<Stat[]>(() => buildStats(props.activeRegion));

    useEffect(() => {
        setStats(buildStats(props.activeRegion));
    }, [props.activeRegion.id, props.activeRegion.stats]);

    const saveStatsHandler = async () => {
        LoginContentAdminApi.saveStats(props.activeRegion.id, stats.filter(s => s.value || s.label))
        .then(() => {
            props.setToast({message: ADMIN_LOGIN_STAT_CONTENT_SAVED_SUCCESS, type: "success"});
            props.onStatsSaved(props.activeRegion.id);
        }).catch(() => {
            props.setToast({message: ADMIN_LOGIN_STAT_CONTENT_SAVED_ERROR, type: "error"});
        });
    };

    const updateStat = (index: number, field: "value" | "label", value: string) => {
        const updated = [...stats];
        updated[index] = {...updated[index], [field]: value};
        setStats(updated);
    };

    return (
        <div className="admin-login-content__panel">
            <p className="admin-login-content__panel-description">
                {ADMIN_LOGIN_STAT_CONTENT_DESCRIPTION}
            </p>
            <div className="admin-login-content__stats-grid">
                {stats.map((stat, index) => (
                    <div key={`stat-${index}`} className="admin-login-content__stat-card">
                        <div className="admin-login-content__form-group">
                            <label className="admin-login-content__label" htmlFor={`alc-stat-val-${index}`}>
                                Value
                            </label>
                            <input
                                id={`alc-stat-val-${index}`}
                                type="text"
                                className="admin-login-content__input admin-login-content__input--center admin-login-content__input--stat-value"
                                value={stat.value}
                                onChange={e => updateStat(index, "value", e.target.value)}
                            />
                        </div>
                        <div className="admin-login-content__form-group">
                            <label className="admin-login-content__label" htmlFor={`alc-stat-lbl-${index}`}>
                                Label
                            </label>
                            <input
                                id={`alc-stat-lbl-${index}`}
                                type="text"
                                className="admin-login-content__input admin-login-content__input--center"
                                value={stat.label}
                                onChange={e => updateStat(index, "label", e.target.value)}
                            />
                        </div>
                        <div className="admin-login-content__stat-preview">
                            <div
                                className="admin-login-content__stat-preview-value"
                                data-testid="admin-login-content__stat-preview-value">
                                {stat.value}
                            </div>
                            <div
                                className="admin-login-content__stat-preview-label"
                                data-testid="admin-login-content__stat-preview-label">
                                {stat.label}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="admin-login-content__actions">
                <RaisedButton
                    type="button"
                    className="admin-login-content__btn admin-login-content__btn--primary"
                    style={prominentButtonStyle}
                    primary={true}
                    onClick={() => void saveStatsHandler()}>
                    <i className="fa-solid fa-check" /> {ADMIN_LOGIN_STAT_CONTENT_SAVE_BTN}
                </RaisedButton>
            </div>
        </div>
    );
};